import { FC } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonBase, Grid, Paper, styled, Theme, Typography, useMediaQuery } from '@mui/material';

import { useToverHead } from 'hooks/useToverHead';
import { stringToSlug } from 'utils/slugify';

interface InspirationCard {
  title: string;
  category: string;
  path: string;
}

const StyledImage = styled('img')({
  width: 200,
  height: 200,
  marginLeft: 'auto',
  marginRight: 'auto',
});

export const NotFound: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  useToverHead({ title: 'error.404.page.heading', noIndex: true });

  const slugOptions = { lower: true, strict: true };

  const cards: InspirationCard[] = [
    {
      title: t('error.404.page.card.module1'),
      category: t('category.introduction'),
      path: `/articles/${stringToSlug(t('error.404.page.card.module1'), slugOptions)}`,
    },
    {
      title: t('error.404.page.card.instruction'),
      category: t('category.videos'),
      path: `/articles/${stringToSlug(t('error.404.page.card.instruction'), slugOptions)}`,
    },
    {
      title: t('error.404.page.card.faq'),
      category: t('category.contact'),
      path: `/categories/${stringToSlug(t('error.404.page.card.faq'), slugOptions)}`,
    },
    {
      title: t('error.404.page.card.contact'),
      category: t('category.contact'),
      path: `/articles/${stringToSlug(t('error.404.page.card.contact'), slugOptions)}`,
    },
  ];

  const onMobile = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'));

  return (
    <Grid id="not-found" container direction="column" justifyContent="space-between">
      <Grid item container justifyContent="center" textAlign="center" m="auto">
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            textAlign: 'center',
            mx: 2,
            my: 4,
          }}
        >
          <StyledImage src={`${process.env.PUBLIC_URL}/images/puzzle.jpg`} alt="puzzle piece" />
          <Typography variant="h1" gutterBottom sx={{ mt: 4 }}>
            {t('error.404.page.heading')}
          </Typography>
          <Typography variant="subtitle2">{t('error.404.page.subtitle')}</Typography>
          {/* The unmatched route — visible so it shows in screenshots (no address
              bar there) and carries a stable id for e2e to read/log. */}
          <Typography id="not-found-path" variant="caption" sx={{ display: 'block', mt: 1, color: 'grey.500' }}>
            {pathname}
          </Typography>
          <Button color="primary" component={RouterLink} fullWidth={!!onMobile} to="/" sx={{ my: 4 }}>
            {t('error.404.page.button')}
          </Button>
        </Grid>
      </Grid>
      <Grid
        item
        container
        sx={{
          bgcolor: 'grey.100',
          pt: 6,
          pb: 10,
          px: {
            xs: 2,
            sm: 4,
          },
        }}
      >
        <Grid item xs={12}>
          <Typography variant="h2" sx={{ mb: 4 }}>
            {t('error.404.page.inspiration')}
          </Typography>
        </Grid>

        <Grid item container spacing={2}>
          {cards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <ButtonBase component={RouterLink} to={card.path} sx={{ height: '100%', width: '100%' }}>
                <Paper
                  square
                  variant="outlined"
                  sx={{
                    'height': '100%',
                    'width': '100%',
                    'p': 2,
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <Typography variant="h4" component="h3" gutterBottom sx={{ overflowWrap: 'break-word' }}>
                    {card.title}
                  </Typography>
                  <Typography>{card.category}</Typography>
                </Paper>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
